import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { Navigate, Route, Routes } from 'react-router-dom';
import ChatLayout from './ChatLayout';
import useChatView from '../hooks/useChatView';
import useNotificationBanner from '../hooks/useNotificationBanner';
import IdleTimerAlert from './IdleTimerAlert';
import ChatHeader from './ChatHeader';
import GettingStartedDialog from './GettingStartedDialog';
import NotificationBanner from './NotificationBanner';
import NotificationBannerDialog from './NotificationBannerDialog';
import ChatList from '../../ChatList/components/ChatList';
import ExploreAgentsPage from '../../Agents/explore/components/ExploreAgentsPage';
import ManageAgentsPage from '../../Agents/management/components/ManageAgentsPage';
import CreateAgentPage from '../../Agents/management/components/CreateAgentPage';
import EditAgentPage from '../../Agents/management/components/EditAgentPage';
import UserManagementPage from '../../UserManagement/components/UserManagementPage';
import SessionWindowRouter from '../../ChatWindow/components/SessionWindowRouter';
import ChatRoom from '../../ChatRoom/components/ChatRoom';
import ServiceInterruptionScreen from './ServiceInterruptionScreen';
import { useAppSelector } from '../../../redux/hooks';

const CHAT_SIDEBAR_EXPANDED_WIDTH = '300px';
const CHAT_SIDEBAR_COLLAPSED_WIDTH = '64px';

const useStyles = makeStyles({
    sidebar: {
        display: 'flex',
        width: '100%',
        minWidth: 0,
        height: '100%',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        backgroundColor: '#f7f7f8',
        borderRight: '1px solid rgba(0, 0, 0, 0.1)',
    },
    mainContent: {
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export const ChatView: React.FC = () => {
    const classes = useStyles();
    const viewModel = useChatView();
    const bannerVm = useNotificationBanner();
    const { isCollapsed, isSmallScreen } = useAppSelector((state) => state.layout);

    const sidebarWidth = isCollapsed || isSmallScreen ? CHAT_SIDEBAR_COLLAPSED_WIDTH : CHAT_SIDEBAR_EXPANDED_WIDTH;

    if (viewModel.isServiceUnavailable) {
        return <ServiceInterruptionScreen />;
    }

    const renderAdminPage = (element: React.ReactElement): React.ReactElement => {
        if (viewModel.isAdmin) {
            return element;
        }

        return <Navigate to="/" replace />;
    };

    return (
        <div data-testid="app-shell">
            <IdleTimerAlert enabled={true} />
            <GettingStartedDialog />
            <NotificationBannerDialog />
            <ChatLayout
                sidebarWidth={sidebarWidth}
                bannerContent={
                    bannerVm.shouldShowBanner ? <NotificationBanner onClose={bannerVm.closeBanner} /> : undefined
                }
                headerContent={
                    <ChatHeader
                        displayName={viewModel.activeUser.displayName}
                        email={viewModel.activeUser.email}
                        isAdmin={viewModel.isAdmin}
                        photoUrl={viewModel.activeUser.photoUrl}
                    />
                }
                sidebarContent={
                    <div className={classes.sidebar}>
                        <ChatList />
                    </div>
                }
                mainContent={
                    <div className={classes.mainContent}>
                        <Routes>
                            <Route path="/" element={<SessionWindowRouter />} />
                            <Route path="/chat/:sessionId" element={<SessionWindowRouter />} />
                            <Route path="/explore-agents" element={<ExploreAgentsPage />} />
                            <Route path="/user-management" element={renderAdminPage(<UserManagementPage />)} />
                            <Route path="/manage-agents" element={renderAdminPage(<ManageAgentsPage />)} />
                            <Route path="/manage-agents/create" element={renderAdminPage(<CreateAgentPage />)} />
                            <Route path="/manage-agents/edit/:agentId" element={renderAdminPage(<EditAgentPage />)} />
                            <Route path="/chat-room/:sessionId" element={<ChatRoom />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </div>
                }
            />
        </div>
    );
};

export default ChatView;
